// Parser helpers for one `@fun: name(args){...}` definition segment.
// Keeps extraction scanner module compact and focused.
import { findMatchingPair, skipWhitespace, splitTopLevelByComma } from '../loops/for-loop/scanner-utils'
import { isValidFunctionIdentifier, resolveLineAt } from './helpers'
import type { LuisFunctionDefinition } from './types'

// Parse one function definition at a known `@fun:` token index.
export function parseFunctionDefinitionAt(
  source: string,
  startIndex: number,
): { definition: LuisFunctionDefinition; endIndex: number } | null {
  let cursor = startIndex + 5
  cursor = skipWhitespace(source, cursor)
  const nameStart = cursor
  // Start one function-name scan after the `@fun:` token.
  while (cursor < source.length && /[a-zA-Z0-9_-]/.test(source[cursor])) {
    cursor += 1
  }
  // Check whether a function name was parsed successfully.
  if (cursor <= nameStart) {
    // Return null for malformed definitions without a function name.
    return null
  } else {
    // Continue parameter list parsing.
  }

  const name = source.slice(nameStart, cursor)
  cursor = skipWhitespace(source, cursor)
  // Check whether the definition contains an opening parameter parenthesis.
  if (source[cursor] !== '(') {
    // Return null for definitions without an opening parameter parenthesis.
    return null
  } else {
    // Continue parameter list extraction.
  }

  const argsEnd = findMatchingPair(source, cursor, '(', ')')
  // Check whether the parameter list closes correctly.
  if (argsEnd === -1) {
    // Return null for definitions with an unclosed parameter list.
    return null
  } else {
    // Continue body parsing for balanced args list.
  }

  const params = parseFunctionParams(source.slice(cursor + 1, argsEnd))
  cursor = skipWhitespace(source, argsEnd + 1)
  // Check whether the definition contains a function body block.
  if (source[cursor] !== '{') {
    // Return null for definitions without a function body block.
    return null
  } else {
    // Continue function body extraction.
  }

  const bodyEnd = findMatchingPair(source, cursor, '{', '}')
  // Check whether the function body closes correctly.
  if (bodyEnd === -1) {
    // Return null for definitions with an unclosed body block.
    return null
  } else {
    // Return parsed function definition descriptor.
    return {
      definition: {
        name,
        params,
        body: source.slice(cursor + 1, bodyEnd),
        line: resolveLineAt(source, startIndex),
      },
      endIndex: bodyEnd + 1,
    }
  }
}

// Parse parameter list into normalized bare parameter names.
function parseFunctionParams(source: string): string[] {
  const rawParams = splitTopLevelByComma(source).map((entry) => entry.trim()).filter(Boolean)
  const params: string[] = []
  // Start one parameter normalization pass and drop invalid names.
  for (const rawParam of rawParams) {
    const normalized = rawParam.startsWith('$') ? rawParam.slice(1).trim() : rawParam
    if (isValidFunctionIdentifier(normalized)) {
      params.push(normalized)
    } else {
      // Skip invalid parameter names silently.
    }
  }

  // Return the normalized parameter name list.
  return params
}
